import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getIslandAreaMapPoints } from 'alakai-aloha-shared';
import { GuidesIndexView, TripPlannerPromoHero } from 'alakai-aloha-shared/public-ui';
import { sanityFetch } from '../../lib/sanity';
import { guideIndexQueries, guideQueries } from '../../lib/sanityQueries';
import { urlFor } from '../../lib/sanityImage';
import { buildRuntimeCanonicalUrl } from '../../lib/siteUrls';
import { getRuntimeSiteContext } from '../../lib/siteContext';

function getImageUrl(image, width = 900, height = 450) {
  if (!image?.asset) return undefined;
  return urlFor(image).width(width).height(height).fit('crop').auto('format').url();
}

function buildGuideSections(guides) {
  const byType = new Map();

  guides.forEach((guide) => {
    const type = guide.pageType || 'other';
    if (!byType.has(type)) byType.set(type, []);
    byType.get(type).push({
      id: guide._id,
      title: guide.title,
      slug: guide.slug,
      heroImageUrl: getImageUrl(guide.heroImage),
      heroImageAlt: guide.heroImage?.alt || guide.title,
    });
  });

  for (const [key, value] of byType.entries()) {
    value.sort((a, b) => {
      const hubRank = (b.hubSpoke === 'hub') - (a.hubSpoke === 'hub');
      if (hubRank !== 0) return hubRank;
      return a.title.localeCompare(b.title);
    });
    byType.set(key, value);
  }

  const combine = (...types) => types.flatMap((type) => byType.get(type) || []).sort((a, b) => a.title.localeCompare(b.title));

  return [
    { id: 'beaches', title: 'Beaches', guides: byType.get('beach') || [] },
    { id: 'hikes', title: 'Hikes', guides: byType.get('hike') || [] },
    { id: 'things-to-do', title: 'Things to Do', guides: combine('tour', 'golf', 'culture', 'shopping') },
    { id: 'where-to-eat', title: 'Where to Eat', guides: byType.get('restaurant') || [] },
    { id: 'where-to-stay', title: 'Where to Stay', guides: byType.get('lodging') || [] },
  ].filter((section) => section.guides.length > 0);
}

export default function GuidesIndex() {
  const location = useLocation();
  const { island, islandConfig } = getRuntimeSiteContext();
  const areaMapPoints = useMemo(() => getIslandAreaMapPoints(island), [island]);
  const [data, setData] = useState({ sections: [], regions: [], areas: [] });
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setStatus('loading');
      try {
        const [guides, regions, areas] = await Promise.all([
          sanityFetch(guideQueries.getAllGuides),
          sanityFetch(guideIndexQueries.getRegionsForIndex),
          sanityFetch(guideIndexQueries.getAreasForIndex),
        ]);

        if (cancelled) return;

        setData({
          sections: buildGuideSections(guides || []),
          regions: (regions || []).map((region) => ({
            id: region._id,
            title: region.title,
            slug: region.slug?.current || region.slug,
            oneLiner: region.oneLiner,
            shortDescription: region.shortDescription,
            heroImageUrl: getImageUrl(region.heroImage),
            heroImageAlt: region.heroImage?.alt || region.title,
          })),
          areas: (areas || []).map((area) => ({
            id: area._id,
            title: area.title,
            slug: area.slug?.current || area.slug,
            oneLiner: area.oneLiner,
            heroImageUrl: getImageUrl(area.heroImage),
            heroImageAlt: area.heroImage?.alt || area.title,
            region: area.region?.title && (area.region?.slug?.current || area.region?.slug)
              ? {
                  title: area.region.title,
                  slug: area.region.slug.current || area.region.slug,
                }
              : undefined,
          })),
        });
        setStatus('ready');
      } catch (error) {
        console.error('Error loading guides index', error);
        if (!cancelled) setStatus('error');
      }
    }

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, []);

  const preset = useMemo(() => new URLSearchParams(location.search).get('preset'), [location.search]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-white/70">Loading guides...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-3">Error loading guides</h1>
          <p className="text-white/60">Please refresh or try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${islandConfig.title} Guides | Alaka'i Aloha`}</title>
        <meta name="description" content={`Browse curated ${islandConfig.title} guides for beaches, hikes, dining, lodging, and local planning.`} />
        <link rel="canonical" href={buildRuntimeCanonicalUrl('/guides')} />
      </Helmet>
      <GuidesIndexView
        islandKey={island}
        sections={data.sections}
        regions={data.regions}
        areas={data.areas}
        areaMapPoints={areaMapPoints}
        mapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
        preset={preset}
        promoSlot={<TripPlannerPromoHero pageType="guide" />}
      />
    </>
  );
}
